import { Chain, ChainDataType, Network } from "@util/constants.ts";

export enum PipelineStatus {
  Created = "created",
  Running = "running",
  Unknown = "unknown",
  Stopped = "stopped",
  Finished = "finished",
  Failed = "failed",
}

export interface Pipeline {
  id: number | string;
  teamId: number | string;
  name: string;
  datasource: {
    name: string;
    network: keyof typeof Network;
  };
  sink: {
    name: string;
    host: string;
    port: string;
    database: string;
  };
  table: {
    schema: string;
    name: string;
  };
  credentials: string;
  status: {
    desired: DesiredPipelineStatus;
    display: DisplayPipelineStatus;
    backfill: ActualPipelineStatus;
    frontfill: ActualPipelineStatus;
  };
  editability: {
    status: boolean;
    reason?: string;
  };
  filters: FiltersParams;
}

export enum DatasourceFilterType {
  ContractAddresses = "contract_addresses",
  StartBlock = "start_block",
  FromAddress = "from_address",
  ToAddress = "to_address",
  ABI = "abi",
}

export interface Datasource {
  name: string;
  network: keyof typeof Network;
  chain: Chain;
  dataType: ChainDataType;
  schema: Array<{
    name: string;
    type: string;
    isNullable: false;
  }>;
  filterTypes: DatasourceFilterType[];
}

export enum DesiredPipelineStatus {
  Unknown = "unknown",
  Running = "running",
  Stopped = "stopped",
}

export enum ActualPipelineStatus {
  Created = "created",
  Running = "running",
  Stopped = "stopped",
}

export enum DisplayPipelineStatus {
  Created = "created",
  Starting = "starting",
  Running = "running",
  Stopped = "stopped",
  Unknown = "unknown",
}

export interface SinkCredentials {
  username: string;
  password: string;
}

export interface FiltersParams {
  contract_addresses?: string[];
  start_block?: number;
  from_address?: string;
  to_address?: string;
  abi?: string;
}

export interface CreatePipelineRequestParams {
  name: string;
  datasource: {
    name: string;
    network: keyof typeof Network;
  };
  sink: {
    name: string;
    host: string;
    port: string;
    database: string;
  };
  table: {
    schema: string;
    name: string;
  };
  credentials: SinkCredentials;
  filters?: FiltersParams;
}

export interface CreatePipelineSuccessResult {
  pipelineId: number;
}

export enum SinkValidationType {
  Connection = "connection",
  SchemaExists = "schema_exists",
  CreateTable = "create_table",
  DropTable = "drop_table",
  InsertRecord = "insert_record",
  DeleteRecord = "delete_record",
}

export enum CreatePipelineFormTarget {
  Connection = "connection",
  Credentials = "credentials",
  Database = "database",
  Table = "table",
  FilterContractAddress = "filter_contract_address",
  FilterFromAddress = "filter_from_address",
  FilterToAddress = "filter_to_address",
  FilterStartBlock = "filter_start_block",
  FilterABI = "filter_abi",
}

export interface CreatePipelineFailureResult {
  validationType: SinkValidationType | "unknown";
  target?: CreatePipelineFormTarget;
  message: string;
}

export interface CreatePipelineDesiredStatusSuccessResult {
  pipeline: Pipeline;
}
